import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { SupportService } from '../shared/support.service';
import {
  SupportCase,
  CreateSupportCaseInput,
  UpdateSupportCaseInput,
} from '../shared/support.model';
import { NotificationService } from '../../shared/services/notification.service';
import { ModalService } from '../../shared/services/modal.service';

@Component({
  standalone: false,
  selector: 'app-instructions',
  templateUrl: 'instructions.component.html',
})
export class InstructionsComponent implements OnInit {
  // Cases
  cases: SupportCase[] = [];
  casesLoading = false;
  casesGridApi: GridApi;

  // Case Form Modal
  showCaseForm = false;
  editingCase: SupportCase | null = null;
  @ViewChild('instructionsTextarea') instructionsTextarea: ElementRef<HTMLTextAreaElement>;
  caseFormModel: CreateSupportCaseInput = { name: '', instructions: '', autoclose: false, sortOrder: 0 };

  // History Modal
  showHistoryModal = false;
  historyCase: SupportCase | null = null;
  historyEntries: any[] = [];
  historyLoading = false;

  casesColumnDefs: ColDef[] = [
    { headerName: 'Name', field: 'name', flex: 2, minWidth: 150 },
    {
      headerName: 'Autoclose',
      field: 'autoclose',
      width: 110,
      cellRenderer: params => {
        const checked = params.value ? 'checked' : '';
        return `<input type="checkbox" class="form-check-input autoclose-toggle" ${checked}>`;
      },
      onCellClicked: params => {
        const target = params.event?.target as HTMLElement;
        if (target && target.classList.contains('autoclose-toggle')) {
          this.toggleAutoclose(params.data);
        }
      },
    },
    { headerName: 'Sort Order', field: 'sortOrder', width: 100 },
    {
      headerName: 'Updated',
      field: 'updatedAt',
      width: 160,
      valueFormatter: params => {
        if (!params.value) return '—';
        return new Date(params.value).toLocaleString();
      },
    },
    {
      headerName: 'Actions',
      width: 200,
      cellRenderer: () => {
        return `
          <button class="btn btn-sm btn-outline-primary me-1 action-edit">Edit</button>
          <button class="btn btn-sm btn-outline-danger me-1 action-delete">Delete</button>
          <button class="btn btn-sm btn-outline-secondary action-history">History</button>
        `;
      },
      onCellClicked: params => {
        const target = params.event?.target as HTMLElement;
        if (!target) return;
        if (target.classList.contains('action-edit')) {
          this.openEditCase(params.data);
        } else if (target.classList.contains('action-delete')) {
          this.deleteCase(params.data);
        } else if (target.classList.contains('action-history')) {
          this.openCaseHistory(params.data);
        }
      },
    },
  ];

  defaultColDef: ColDef = {
    resizable: true,
    sortable: true,
    suppressMenu: true,
    cellClass: 'd-flex justify-content-start align-items-center',
  };

  constructor(
    private supportService: SupportService,
    private notificationService: NotificationService,
    private modal: ModalService,
  ) {}

  ngOnInit() {
    this.loadCases();
  }

  // ─── Cases ─────────────────────────────────────────────────────────

  onCasesGridReady(event: GridReadyEvent) {
    this.casesGridApi = event.api;
  }

  loadCases() {
    this.casesLoading = true;
    this.supportService.getCases().subscribe({
      next: res => {
        this.cases = res.items;
        this.casesLoading = false;
      },
      error: () => {
        this.casesLoading = false;
        this.notificationService.error('Failed to load cases', 'Support');
      },
    });
  }

  openCreateCase() {
    this.editingCase = null;
    this.caseFormModel = { name: '', instructions: '', autoclose: false, sortOrder: 0 };
    this.showCaseForm = true;
    this.autoResizeInstructions();
  }

  openEditCase(c: SupportCase) {
    this.editingCase = c;
    this.caseFormModel = {
      name: c.name,
      instructions: c.instructions,
      autoclose: c.autoclose,
      sortOrder: c.sortOrder,
    };
    this.showCaseForm = true;
    this.autoResizeInstructions();
  }

  autoResizeInstructions(textarea?: HTMLTextAreaElement) {
    setTimeout(() => {
      const el = textarea || this.instructionsTextarea?.nativeElement;
      if (el) {
        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
        el.style.overflowY = el.scrollHeight > el.clientHeight ? 'auto' : 'hidden';
      }
    });
  }

  closeCaseForm() {
    this.showCaseForm = false;
    this.editingCase = null;
  }

  saveCase() {
    if (this.editingCase) {
      const update: UpdateSupportCaseInput = { ...this.caseFormModel };
      this.supportService.updateCase(this.editingCase.id, update).subscribe({
        next: () => {
          this.notificationService.success('Case updated', 'Support');
          this.closeCaseForm();
          this.loadCases();
        },
        error: () => {
          this.notificationService.error('Failed to update case', 'Support');
        },
      });
    } else {
      this.supportService.createCase(this.caseFormModel).subscribe({
        next: () => {
          this.notificationService.success('Case created', 'Support');
          this.closeCaseForm();
          this.loadCases();
        },
        error: () => {
          this.notificationService.error('Failed to create case', 'Support');
        },
      });
    }
  }

  deleteCase(c: SupportCase) {
    this.modal
      .confirm()
      .open()
      .result.then(confirmed => {
        if (!confirmed) return;
        this.supportService.deleteCase(c.id).subscribe({
          next: () => {
            this.notificationService.success('Case deleted', 'Support');
            this.loadCases();
          },
          error: () => {
            this.notificationService.error('Failed to delete case', 'Support');
          },
        });
      });
  }

  toggleAutoclose(c: SupportCase) {
    this.supportService.toggleAutoclose(c.id, !c.autoclose).subscribe({
      next: () => {
        this.notificationService.success(`Autoclose ${!c.autoclose ? 'enabled' : 'disabled'} for ${c.name}`, 'Support');
        this.loadCases();
      },
      error: () => {
        this.notificationService.error('Failed to toggle autoclose', 'Support');
      },
    });
  }

  openCaseHistory(c: SupportCase) {
    this.historyCase = c;
    this.historyEntries = [];
    this.historyLoading = true;
    this.showHistoryModal = true;

    this.supportService.getCaseHistory(c.id).subscribe({
      next: res => {
        this.historyEntries = res.items;
        this.historyLoading = false;
      },
      error: () => {
        this.historyLoading = false;
        this.notificationService.error('Failed to load case history', 'Support');
      },
    });
  }

  closeHistoryModal() {
    this.showHistoryModal = false;
    this.historyCase = null;
    this.historyEntries = [];
  }
}
